import { EVENT, send } from "@/lib/amplitude";
import ProfileImage from "@/modules/common/profile";
import styled from "@emotion/styled";
import { useTranslation } from "next-i18next";
import { useMemo } from "react";

interface Users {
  dub_user_flag: string;
  idx: number;
  level: any;
  membership: "S" | "V" | "P" | "F";
  name: string;
  profile_url: string;
  unit: any;
}

const DubbedProfile = ({
  idx,
  itemSeq,
  users,
  together_flag,
  setShowPopup,
  activeNumber,
  scriptData,
  handlePause,
}: {
  idx: number;
  itemSeq: number;
  users: Array<Users>;
  together_flag: boolean;
  setShowPopup: any;
  activeNumber: number;
  scriptData: any[];
  handlePause: () => void;
}) => {
  const { t } = useTranslation();

  const handleClick = (event: any) => {
    send(EVENT.CLICK__PROFILE_AREA_ON_DUBBED_PAGE, {
      dubbed_idx: idx,
      target_user_idx: users[0]?.idx ?? "",
      is_host: false,
      item_seq: itemSeq,
    });

    event.stopPropagation();
    handlePause();
    setShowPopup(true);
  };

  const currentUserIdx = useMemo(() => {
    if (!together_flag) {
      return users[0].idx;
    }

    if (activeNumber === 0) return 0;

    if (scriptData[activeNumber - 1]?.user?.idx) {
      return scriptData[activeNumber - 1].user.idx;
    }

    return 0;
  }, [activeNumber, scriptData, together_flag, users]);

  if (together_flag) {
    const isHost = users[0].idx === currentUserIdx;
    const isGuest = users[1].idx === currentUserIdx;
    const isReady = currentUserIdx === 0;
    const isFocus = {
      zIndex: 6,
    };
    const isBlur = {
      opacity: 0.4,
      zIndex: 4,
    };

    return (
      <Section>
        <Wrapper>
          <ProfileImageContainer
            onClick={handleClick}
            together_flag={together_flag}
          >
            <ProfileImage
              size="small"
              src={users[0].profile_url}
              membership={users[0].membership}
              alt="Profile"
              style={{
                position: "relative",
                ...(isHost ? isFocus : isBlur),
                ...(isReady && {
                  opacity: 1,
                  zIndex: 6,
                }),
              }}
            />
            <ProfileImage
              size="small"
              src={users[1].profile_url}
              membership={users[1].membership}
              alt="Profile"
              style={{
                position: "relative",
                transform: "translate(-14px, 0)",
                ...(isGuest ? isFocus : isBlur),
                ...(isReady && {
                  opacity: 1,
                  zIndex: 4,
                }),
              }}
            />
          </ProfileImageContainer>
        </Wrapper>
        <ProfileInfoContainer>
          <NickName>{`${users[0].name} + ${users[1].name}`}</NickName>
        </ProfileInfoContainer>
      </Section>
    );
  }

  return (
    <Section>
      <Wrapper onClick={handleClick}>
        <ProfileImageContainer together_flag={together_flag}>
          <ProfileImage
            size="small"
            src={users[0].profile_url}
            membership={users[0].membership}
            alt="Profile"
          />
        </ProfileImageContainer>
        <ProfileInfoContainer>
          <NickName>{users[0].name}</NickName>
          <Level>{t(`level_${users[0].level.idx}_title`)}</Level>
        </ProfileInfoContainer>
      </Wrapper>
    </Section>
  );
};

const Section = styled.div`
  position: relative;

  width: 100%;
  height: 88px;
  padding: 0 20px;

  display: flex;
  align-items: center;
`;

const Wrapper = styled.div`
  position: relative;
  z-index: 3;
  display: flex;
  align-items: center;
`;

const ProfileImageContainer = styled.div<{ together_flag: boolean }>`
  ${({ together_flag }) => (together_flag ? `width: 66px;` : `width: 40px;`)}
  position: relative;
  display: flex;
  align-items: center;
`;

const ProfileInfoContainer = styled.div`
  margin-left: 8px;
`;

const NickName = styled.p`
  font-size: var(--fs-b2);
  line-height: var(--lh-b2);
  font-weight: 400;
  color: var(--white);
`;

const Level = styled.p`
  font-size: var(--fs-c1);
  line-height: var(--lh-c1);
  font-weight: 400;
  color: var(--subBlue);
`;

export default DubbedProfile;
